import React, { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Stepper } from './Stepper';

/**
 * @example ```
 * <SimpleStepper current='step-one'>
 *     <SimpleStepper.Step name='step-one' isHidden/>
 *     <SimpleStepper.Step name='step-two' isDisabled />
 *     <SimpleStepper.Step name='step-three' isDisabled />
 * </SimpleStepper>
 * ```
 */
export const SimpleStepper = ({
    current, navigateTo,
    className, hide, children: steps
}: Props) => {

    const { t } = useTranslation();
    const children = selectReactElements<StepProps>(steps);
    const currentIndex = children.findIndex(c => c.props.name === current);

    const onClick = (index: number) => {
        if (navigateTo) {
            navigateTo(index);
        }
    };

    return (
        <Stepper hide={hide} className={className}>
            {children.map((child, index) => !child.props.isHidden && (
                <Stepper.Step key={child.props.name} onClick={() => onClick(index)}
                    phase={Stepper.determinePhase(index, currentIndex)}
                    disabled={child.props.isDisabled}>
                    {t(child.props.name)}
                </Stepper.Step>
            ))}
        </Stepper>
    );
};

/*
 * Dummy component whose props are used by the [`<SimpleStepper />`]{@link SimpleStepper} component to
 * create [`<Stepper.Step />`]{@link Stepper.Step}.
 */
SimpleStepper.Step = (_: StepProps) => null;

const selectReactElements = <TTo extends Record<string, any>>(children: ReactNode[]) => {
    return React.Children
        .toArray(children)
        .filter(c => Boolean(c)) as ReactElement<TTo>[];
};

type StepProps = {
    name: string;
    isDisabled?: boolean;
    isHidden?: boolean;
};

type Props = {
    className?: string;
    children: (ReactElement<StepProps> | null | false | undefined)[];
    current: string;
    navigateTo?: (index: number) => void;
    hide?: boolean;
};